import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { ReportAgentResponseV4Schema } from "../response/ReportAgentResponseV4";

const API_VERSION = 'v4';

// Tipe spesifik untuk kolom yang bisa diurutkan
type OrderByOption = "agent_name" | "percentage" | "room_nights" | "reservations" | "avg_nights" | "booking_window" | "revenue" | "ota_com" | "avg_rate" | "cancel_count";

// Antarmuka untuk parameter filter di body request
interface FilterPropsV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "stay_date" | "created_reservation";
    room_type_id?: string;
}

// Antarmuka untuk parameter hook
interface ReportAgentV4Props {
    filter: FilterPropsV4;
    orderBy?: OrderByOption; // Menggunakan tipe yang sudah didefinisikan
    sort?: "asc" | "desc";
    type: string | null;
}

/**
 * Hook React Query untuk mengambil laporan agen v4.
 * Versi ini menggunakan request POST dan menangani struktur respons bersarang.
 *
 * @param params - Filter, opsi pengurutan, dan tipe laporan.
 * @returns Hasil dari hook useQuery.
 */
const useReportAgentV4 = (params: ReportAgentV4Props) => {
    return useQuery({
        queryKey: [
            'report',
            'report-agent-v4',
            params.type,
            JSON.stringify(params.filter),
            params.orderBy,
            params.sort,
        ],
        queryFn: async () => {
            try {
                // Menyusun body request
                const requestBody = {
                    filter: params.filter,
                    orderBy: params.orderBy || "agent_name", // orderBy default
                    sort: params.sort || "desc", // sort default
                };

                const response = await privateApi.get(`/${API_VERSION}/channel-manager/property-report-agent`, { params: requestBody });

                // Validasi respons dengan skema Zod
                const validationResult = ReportAgentResponseV4Schema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid V4 Agent report data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch V4 Report agent`, error);
                throw error;
            }
        },
        enabled: !!params.filter && !!params.filter.property_id && !!params.filter.start_date && !!params.filter.end_date && params.type === "report_agent",
        retry: 1,
        refetchOnWindowFocus: false,
    });
};

export default useReportAgentV4;